import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    LinearProgress,
    Typography,
    Box,
} from '@mui/material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import type { ObjectSchema } from 'yup';

type UploadDocumentModalProps = {
    open: boolean;
    onClose: () => void;
    onUpload: (values: FormValues) => Promise<void>;
    isLoading: boolean;
};

export type FormValues = {
    title: string;
    file: File | null;
};

const validationSchema: ObjectSchema<FormValues> = Yup.object({
    title: Yup.string().required('Document title is required'),
    file: Yup.mixed<File>()
        .nullable()
        .required('A file is required')
        .test('is-file', 'A file is required', (value) => value instanceof File),
});

function UploadDocumentModal({ open, onClose, onUpload, isLoading }: UploadDocumentModalProps) {
    const { control, handleSubmit, formState: { errors }, watch, reset } = useForm<FormValues>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            title: '',
            file: null,
        }
    });

    const fileValue = watch('file');

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        await onUpload(data);
        reset();
    };

    const handleClose = () => {
        reset();
        onClose();
    }

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Upload Document</DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Document Title"
                                fullWidth
                                margin="dense"
                                error={!!errors.title}
                                helperText={errors.title?.message}
                            />
                        )}
                    />
                    <Controller
                        name="file"
                        control={control}
                        render={({ field: { onChange } }) => (
                            <>
                                <Button
                                    variant="contained"
                                    component="label"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                >
                                    Choose File
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(event) => {
                                            onChange(event.currentTarget.files?.[0] || null);
                                        }}
                                    />
                                </Button>
                            </>
                        )}
                    />

                    {fileValue && <Typography sx={{ mt: 1 }}>{fileValue.name}</Typography>}
                    {isLoading && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                            <LinearProgress />
                            <Typography align="center" sx={{ mt: 1 }}>Uploading...</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={!fileValue || isLoading}>
                        {isLoading ? `Uploading...` : 'Upload and Process'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default UploadDocumentModal; 